// plugins/jsonld.ts
export default defineNuxtPlugin(() => {
  const DEFAULT_IMAGES = [
    'https://mietfotobox.com/static/assets/sample-1.webp',
    'https://mietfotobox.com/static/assets/sample-2.webp',
    'https://mietfotobox.com/static/assets/sample-3.webp',
    'https://mietfotobox.com/static/assets/sample-4.webp',
    'https://mietfotobox.com/static/assets/sample-5.webp',
    'https://mietfotobox.com/static/assets/sample-6.webp'
  ]

  const DEFAULT_PROVIDER = {
    '@type': 'Organization',
    name: 'mietfotobox',
    url: 'https://mietfotobox.com',
    telephone: '+4917641620316',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Am Dörferweg 14',
      addressLocality: 'Berlin',
      postalCode: '13057',
      addressCountry: 'DE'
    }
  }

  const cleanObject = (obj: any): any => {
    if (obj === null || obj === undefined || obj === '') return null

    if (typeof obj !== 'object') return obj

    if (Array.isArray(obj)) {
      const cleanedArray = obj
        .map(cleanObject)
        .filter((item) => item !== null && item !== undefined)

      return cleanedArray.length > 0 ? cleanedArray : null
    }

    const result: Record<string, any> = {}

    for (const key in obj) {
      const value = cleanObject(obj[key])

      if (value !== null && value !== undefined) {
        result[key] = value
      }
    }

    return Object.keys(result).length > 0 ? result : null
  }

  const normalizeOpeningHours = (openingHours: any) => {
    if (!openingHours) return {}

    // Falls bereits ein sauberer String wie "Mo-Su 08:00-22:00" übergeben wird
    if (typeof openingHours === 'string') {
      return {
        openingHours
      }
    }

    // Falls bereits ein Array von OpeningHoursSpecification-Objekten übergeben wird
    if (Array.isArray(openingHours)) {
      return {
        openingHoursSpecification: openingHours.map((item) => ({
          '@type': 'OpeningHoursSpecification',
          ...item
        }))
      }
    }

    // Falls ein einzelnes Objekt übergeben wird
    if (typeof openingHours === 'object') {
      return {
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            ...openingHours
          }
        ]
      }
    }

    return {}
  }

  const normalizeAggregateRating = (aggregateRating: any) => {
    if (
      !aggregateRating ||
      aggregateRating.ratingValue === undefined ||
      aggregateRating.reviewCount === undefined
    ) {
      return null
    }

    return {
      '@type': 'AggregateRating',
      ratingValue: Number(aggregateRating.ratingValue),
      reviewCount: Number(aggregateRating.reviewCount),
      bestRating: 5,
      worstRating: 1
    }
  }

  const normalizeOffer = (serviceInfo: any) => {
    if (serviceInfo.offers && typeof serviceInfo.offers === 'object') {
      return {
        '@type': 'Offer',
        ...serviceInfo.offers
      }
    }

    if (serviceInfo.price) {
      return {
        '@type': 'Offer',
        price: String(serviceInfo.price),
        priceCurrency: serviceInfo.priceCurrency || 'EUR',
        availability: serviceInfo.availability || 'https://schema.org/InStock',
        url: serviceInfo.url || 'https://mietfotobox.com'
      }
    }

    // Wenn nur eine Range bekannt ist, lieber kein halbleeres Offer mit priceRange bauen
    return null
  }

    const getDefaultProvider = () => ({
        '@type': 'Organization',
        name: 'mietfotobox',
        url: 'https://mietfotobox.com',
        telephone: '+4917641620316',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Am Dörferweg 14',
          addressLocality: 'Berlin',
          postalCode: '13057',
          addressCountry: 'DE'
        }
      })

  const isValidFaqEntry = (faq: any) => {
    if (!faq || typeof faq !== 'object') return false

    const question = faq.question?.trim?.()
    const answer = faq.answer?.trim?.()

    if (!question || !answer) return false

    // simple Guards gegen kaputte Generator-Texte
    if (answer.includes(':.')) return false
    if (answer.length < 20) return false

    return true
  }

  return {
    provide: {
      generateLocalBusinessSchema: (businessInfo: any = {}) => {
        const hasGeo =
          businessInfo.geo?.latitude !== undefined &&
          businessInfo.geo?.latitude !== null &&
          businessInfo.geo?.latitude !== '' &&
          businessInfo.geo?.longitude !== undefined &&
          businessInfo.geo?.longitude !== null &&
          businessInfo.geo?.longitude !== ''

        const schema = {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': businessInfo.id || 'https://mietfotobox.com/#localbusiness',
          name: businessInfo.name || 'mietfotobox',
          description:
            businessInfo.description || 'Fotobox Vermietung in Berlin',
          url: businessInfo.url || 'https://mietfotobox.com',
          telephone: businessInfo.telephone || '+4917641620316',
          image: businessInfo.image || DEFAULT_IMAGES,
          address: {
            '@type': 'PostalAddress',
            streetAddress: businessInfo.address?.street || 'Am Dörferweg 14',
            addressLocality: businessInfo.address?.city || 'Berlin',
            postalCode: businessInfo.address?.zip || '13057',
            addressCountry: businessInfo.address?.country || 'DE'
          },
          geo: hasGeo
            ? {
                '@type': 'GeoCoordinates',
                latitude: businessInfo.geo.latitude,
                longitude: businessInfo.geo.longitude
              }
            : null,
          priceRange: businessInfo.priceRange || '€-€€',
          ...normalizeOpeningHours(
            businessInfo.openingHours || 'Mo-Su 08:00-22:00'
          )
        }

        return cleanObject(schema)
      },

      generateFaqSchema: (faqs: any[] = []) => {
        const validFaqs = Array.isArray(faqs) ? faqs.filter(isValidFaqEntry) : []

        const schema = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: validFaqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question.trim(),
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer.trim()
            }
          }))
        }

        return cleanObject(schema)
      },

      generateServiceSchema: (serviceInfo: any = {}) => {
        const schema = {
          '@context': 'https://schema.org',
          '@type': 'Service',
          '@id':
            serviceInfo.id ||
            `${serviceInfo.url || 'https://mietfotobox.com'}#service`,
          name: serviceInfo.name || 'Fotobox Vermietung',
          description:
            serviceInfo.description || 'Professionelle Fotobox für Events',
          url: serviceInfo.url || 'https://mietfotobox.com',
          image: serviceInfo.image || DEFAULT_IMAGES,
          provider: getDefaultProvider(),
          areaServed: serviceInfo.areaServed
            ? typeof serviceInfo.areaServed === 'string'
              ? {
                  '@type': 'City',
                  name: serviceInfo.areaServed
                }
              : serviceInfo.areaServed
            : {
                '@type': 'City',
                name: 'Berlin'
              },
          offers: normalizeOffer(serviceInfo),
          aggregateRating: normalizeAggregateRating(serviceInfo.aggregateRating)
        }

        return cleanObject(schema) || {}
      },

      generateBreadcrumbSchema: (items: any[] = []) => {
        const validItems = Array.isArray(items)
          ? items.filter((item) => item?.name && item?.item)
          : []

        const schema = {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: validItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.item
          }))
        }

        return cleanObject(schema) || {}
      }
    }
  }
})
